import { useEffect, useState } from 'react';
import api, { formatApiError, API } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Briefcase, Plus, MapPin, Users, FileText, ArrowRight, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const STAGES = ['applied', 'screening', 'interview', 'offer', 'hired', 'rejected'];
const stageColors = {
  applied: 'bg-slate-100 text-slate-700',
  screening: 'bg-blue-50 text-blue-700',
  interview: 'bg-violet-50 text-violet-700',
  offer: 'bg-amber-50 text-amber-700',
  hired: 'bg-emerald-50 text-emerald-700',
  rejected: 'bg-red-50 text-red-700',
};

export default function Recruitment() {
  const nav = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: '', department: '', location: 'Remote', employment_type: 'fulltime',
    grade: '', salary_min: '', salary_max: '', description: '', requirements: '',
  });
  const [activeJobId, setActiveJobId] = useState(null);

  const load = async () => {
    const [j, a] = await Promise.all([api.get('/jobs'), api.get('/applications')]);
    setJobs(j.data); setApplications(a.data);
  };
  useEffect(() => { load(); }, []);

  const createJob = async () => {
    if (!form.title || !form.description) return toast.error('Title and description required');
    try {
      const body = { ...form };
      ['salary_min', 'salary_max'].forEach(k => { body[k] = body[k] ? +body[k] : null; });
      ['grade', 'requirements', 'department'].forEach(k => { if (!body[k]) delete body[k]; });
      await api.post('/jobs', body);
      toast.success('Job posted'); setOpen(false);
      setForm({ title: '', department: '', location: 'Remote', employment_type: 'fulltime', grade: '', salary_min: '', salary_max: '', description: '', requirements: '' });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleJob = async (j) => {
    try {
      await api.patch(`/jobs/${j.id}`, { status: j.status === 'open' ? 'closed' : 'open' });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteJob = async (id) => {
    if (!window.confirm('Delete this job?')) return;
    try { await api.delete(`/jobs/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const moveStage = async (app, stage) => {
    try {
      await api.patch(`/applications/${app.id}/stage`, { stage });
      toast.success(`Moved to ${stage}`); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const convert = async (app) => {
    try {
      const { data } = await api.get(`/applications/${app.id}/prefill`);
      const params = new URLSearchParams();
      Object.entries(data).forEach(([k, v]) => { if (v != null) params.set(k, v); });
      toast.success('Loaded prefill — review and create employee');
      nav(`/employees?prefill=${encodeURIComponent(JSON.stringify(data))}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filteredApps = activeJobId ? applications.filter(a => a.job_id === activeJobId) : applications;

  return (
    <div className="space-y-6" data-testid="recruitment-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Hiring</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">Recruitment</h1>
          <p className="text-slate-500 mt-1">{jobs.filter(j => j.status === 'open').length} open · {applications.length} applications</p>
          <div className="mt-2 text-xs">
            <a href="/jobs" target="_blank" rel="noreferrer" className="text-[#3D4A8E] hover:underline">
              View public job board → /jobs
            </a>
          </div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-job-button" className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0] text-white px-6 h-11">
              <Plus className="w-4 h-4 mr-2" /> Post job
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="new-job-dialog">
            <DialogHeader><DialogTitle className="font-heading">Post a new job</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Title *</Label>
                  <Input data-testid="job-title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="rounded-xl mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Department</Label>
                  <Input data-testid="job-department" value={form.department} onChange={(e) => setForm({...form, department: e.target.value})} className="rounded-xl mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Location</Label>
                  <Input data-testid="job-location" value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} className="rounded-xl mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Type</Label>
                  <Select value={form.employment_type} onValueChange={(v) => setForm({...form, employment_type: v})}>
                    <SelectTrigger data-testid="job-type" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fulltime">Full-time</SelectItem>
                      <SelectItem value="contract">Contract</SelectItem>
                      <SelectItem value="intern">Intern</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Grade</Label>
                  <Select value={form.grade} onValueChange={(v) => setForm({...form, grade: v})}>
                    <SelectTrigger data-testid="job-grade" className="rounded-xl mt-1"><SelectValue placeholder="L1-L5" /></SelectTrigger>
                    <SelectContent>
                      {['L1','L2','L3','L4','L5'].map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div /> {/* spacer */}
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Salary min (₹/yr)</Label>
                  <Input data-testid="job-salary-min" type="number" value={form.salary_min} onChange={(e) => setForm({...form, salary_min: e.target.value})} className="rounded-xl mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Salary max (₹/yr)</Label>
                  <Input data-testid="job-salary-max" type="number" value={form.salary_max} onChange={(e) => setForm({...form, salary_max: e.target.value})} className="rounded-xl mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Description *</Label>
                <Textarea data-testid="job-description" rows={4} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="rounded-xl mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Requirements</Label>
                <Textarea data-testid="job-requirements" rows={3} value={form.requirements} onChange={(e) => setForm({...form, requirements: e.target.value})} className="rounded-xl mt-1" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
              <Button onClick={createJob} data-testid="submit-job" className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">Publish job</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="jobs">
        <TabsList className="rounded-full bg-slate-100 p-1 h-auto">
          <TabsTrigger value="jobs" className="rounded-full px-5 py-2 data-[state=active]:bg-[#3D4A8E] data-[state=active]:text-white" data-testid="tab-jobs">Jobs ({jobs.length})</TabsTrigger>
          <TabsTrigger value="pipeline" className="rounded-full px-5 py-2 data-[state=active]:bg-[#3D4A8E] data-[state=active]:text-white" data-testid="tab-pipeline">Candidates ({applications.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="jobs" className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          {jobs.map(j => {
            const appsCount = applications.filter(a => a.job_id === j.id).length;
            return (
              <div key={j.id} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`job-${j.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-heading font-semibold text-lg text-slate-900">{j.title}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-2 mt-1 flex-wrap">
                      <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" /> {j.department || '—'}</span>
                      <span>·</span>
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {j.location}</span>
                      <span>·</span>
                      <span>{j.employment_type}</span>
                      {j.grade && <><span>·</span><span>{j.grade}</span></>}
                    </div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full border ${j.status === 'open' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                    {j.status}
                  </span>
                </div>
                <div className="text-sm text-slate-600 mt-3 line-clamp-2">{j.description}</div>
                <div className="mt-3 flex items-center gap-2 text-xs">
                  <span className="text-slate-500 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {appsCount} applicant{appsCount !== 1 ? 's' : ''}</span>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setActiveJobId(j.id); document.querySelector('[data-testid="tab-pipeline"]')?.click(); }}
                          data-testid={`view-applicants-${j.id}`} className="rounded-full">View applicants</Button>
                  <Button size="sm" variant="outline" onClick={() => toggleJob(j)} data-testid={`toggle-job-${j.id}`} className="rounded-full">
                    {j.status === 'open' ? 'Close' : 'Reopen'}
                  </Button>
                  <button onClick={() => deleteJob(j.id)} data-testid={`delete-job-${j.id}`}
                          className="ml-auto text-slate-400 hover:text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </TabsContent>

        <TabsContent value="pipeline" className="mt-5 space-y-4">
          {activeJobId && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-500">Filtered to job:</span>
              <span className="font-medium">{jobs.find(j => j.id === activeJobId)?.title}</span>
              <button onClick={() => setActiveJobId(null)} className="text-[#3D4A8E] hover:underline text-xs">Clear</button>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredApps.map(a => (
              <div key={a.id} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`app-${a.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-heading font-semibold text-slate-900">{a.name}</div>
                    <div className="text-xs text-slate-500">{a.email} · {a.phone || 'no phone'}</div>
                    <div className="text-xs text-slate-500 mt-1">Applied for <b>{a.job_title}</b></div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${stageColors[a.stage]}`}>{a.stage}</span>
                </div>
                {a.cover_letter && <div className="mt-2 text-sm text-slate-600 line-clamp-2 italic">"{a.cover_letter}"</div>}
                <div className="mt-3 flex gap-2 items-center flex-wrap">
                  {a.resume_file_id && (
                    <a href={`${API}/files/${a.resume_file_id}?auth=${localStorage.getItem('hrms_token')}`}
                       target="_blank" rel="noreferrer"
                       className="text-xs text-[#3D4A8E] hover:underline flex items-center gap-1">
                      <FileText className="w-3.5 h-3.5" /> Resume
                    </a>
                  )}
                  {a.linkedin_url && <a href={a.linkedin_url} target="_blank" rel="noreferrer" className="text-xs text-[#3D4A8E] hover:underline">LinkedIn ↗</a>}
                </div>
                <div className="mt-4 flex gap-2 flex-wrap">
                  <Select value={a.stage} onValueChange={(v) => moveStage(a, v)}>
                    <SelectTrigger data-testid={`stage-${a.id}`} className="rounded-full h-9 w-32 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  {(a.stage === 'offer' || a.stage === 'hired') && (
                    <Button size="sm" onClick={() => convert(a)} data-testid={`convert-${a.id}`}
                            className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">
                      <ArrowRight className="w-3.5 h-3.5 mr-1" /> Convert to employee
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {filteredApps.length === 0 && (
              <div className="col-span-full text-center py-12 text-slate-400">No applications {activeJobId ? 'for this job' : 'yet'}.</div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
